import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { authAPI } from '../services/api';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await authAPI.forgotPassword(email);
      console.log('Password reset requested:', response.data);
      setSubmitted(true);
    } catch (err: any) {
      console.error('Error:', err);
      const errorMsg = err.response?.data?.error || 'Failed to send reset email. Please try again.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="fp-page">
        {/* Animated Background Orbs */}
        <div className="fp-orb fp-orb-1"></div>
        <div className="fp-orb fp-orb-2"></div>
        <div className="fp-orb fp-orb-3"></div>
        
        {/* Floating Particles */}
        <div className="fp-particle fp-particle-1"></div>
        <div className="fp-particle fp-particle-2"></div>
        <div className="fp-particle fp-particle-3"></div>
        <div className="fp-particle fp-particle-4"></div>
        <div className="fp-particle fp-particle-5"></div>
        <div className="fp-particle fp-particle-6"></div>
        
        {/* Tech Shapes */}
        <div className="fp-shape fp-shape-hex"></div>
        <div className="fp-shape fp-shape-triangle"></div>
        <div className="fp-shape fp-shape-circle"></div>

        <div className="fp-container">
          <div className="fp-card">
            <div className="fp-header">
              <button className="fp-back-btn" onClick={() => navigate('/login')}>
                ← Back to Login
              </button>
              <h1 className="fp-title">Forgot Password?</h1>
              <p className="fp-subtitle">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {error && (
              <div className="fp-error-alert">
                <div className="fp-error-message">
                  <span className="fp-error-icon">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            {submitted ? (
              <div className="fp-success-message">
                <div className="fp-success-icon">✓</div>
                <h3>Check Your Email</h3>
                <p>
                  We've sent a password reset link to <strong>{email}</strong>
                </p>
                <p className="fp-success-note">
                  Please check your inbox and follow the instructions to reset your password.
                </p>
                <button className="fp-back-to-login" onClick={() => navigate('/login')}>
                  Return to Login
                </button>
              </div>
            ) : (
              <form className="fp-form" onSubmit={handleSubmit}>
                <div className="fp-form-group">
                  <label className="fp-form-label">Email Address</label>
                  <input
                    type="email"
                    className="fp-form-input"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    autoFocus
                  />
                </div>

                <button type="submit" className="fp-reset-btn" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}

            <div className="fp-footer">
              <p>
                Remember your password? <a href="/login">Sign In</a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ForgotPassword;